/**
 * `IRealtimeTransport.ts` — the contract every realtime transport implements.
 *
 * Unity parity: upstream keeps its own `IRealtimeTransport.cs` for the same
 * reason — it has two implementations too (`AIAgentTransportWebSocket.cs`, and
 * the unwired `AIRealtimeTransportModelRTC.cs`). The web now ships both:
 *
 *   - `AIRealtimeTransportModelRTC` — WebRTC. **The default since 2026-08-28.**
 *   - {@link AIRealtimeTransportModel} — WebSocket + base64 PCM16 @ 24 kHz.
 *     The only path Unity's session host wires, and what the account-wide
 *     `webRtcRealtimeTransport` dev setting falls back to when switched off —
 *     also where a failed WebRTC connect lands, stickily, for the app session.
 *
 * `AIRealtimeSessionModel.connectAsync` picks one per connect from
 * `resolveSessionConnection()`. **Nothing above this interface may branch on
 * which one is live** — that is the whole point of it, and it is why the four
 * members with no natural WebRTC equivalent are documented here rather than
 * left to be rediscovered from call sites.
 *
 * ## Contract facts a second implementation must honour
 *
 * Each of these fails SILENTLY, in a different subsystem, if got wrong:
 *
 * 1. **`connectAsync` must not reject.** `AIRealtimeSessionModel` awaits it
 *    bare and the boolean is the sole input to its teardown-vs-promote branch,
 *    so a rejection skips both — the session is neither torn down nor promoted
 *    to `connected`, and no reconnect is scheduled. Resolve `false` and emit
 *    the reason on {@link onError} instead.
 * 2. **`connectAsync` needs its own deadline.** The WebSocket path gets away
 *    without one because a TLS connect fails fast; ICE can sit in `checking`
 *    indefinitely with no event at all. Without a deadline Spark sits on
 *    "connecting" forever and the sticky-fallback counter never increments.
 * 3. **`sendEvent` must not throw.** It is called from inside
 *    `RealtimeSession.executePendingCall`, where a throw abandons a
 *    `function_call_output` and leaves the model waiting for a result that
 *    never arrives. Catch and drop, as the WebSocket implementation does.
 * 4. **`onAgentSpeakingChanged` must exist from construction.**
 *    `AIRealtimeSessionModel.wireSession` subscribes to it BEFORE awaiting
 *    `connectAsync`, so an emitter created lazily (in `pc.ontrack`, say) hands
 *    that subscription a dead object and the activity latch never releases —
 *    Spark's 3D crown drops mid-answer and the Stop button disappears while it
 *    is still talking.
 * 5. **`isConnected` means "a client event sent right now will arrive"**, not
 *    "the underlying connection object exists". `RealtimeSession` guards all
 *    four of its send paths on it. Over WebRTC that is the DATA CHANNEL's
 *    `readyState === "open"`; a peer-connection-level reading makes every guard
 *    pass while every send throws `InvalidStateError`.
 *
 * ## Members with no consumer
 *
 * `disconnect()` and `micInputLevel` are called by nothing in the tree
 * (whole-tree grep, 2026-08-28). `disconnect()` is retained because `dispose()`
 * is defined in terms of it and the pair reads as one idea; `micInputLevel` is
 * the seam the deferred Spark voice-visualizer bars would land on. A new
 * implementation should alias / return 0 rather than build a level meter
 * nothing renders.
 */

import type { Emitter } from "@core/events/emitter";

/** Which transport a session connects with — see the file header. */
export type RealtimeTransportKind = "websocket" | "webrtc";

/** Why the microphone gate changed, carried into the realtime conversation log. */
export type RealtimeMicrophoneReason =
  | "push_to_talk"
  | "constant_listening"
  | "manual"
  | "session_sync";

/**
 * Codes a transport emits on {@link IRealtimeTransport.onError} to mean "this
 * transport could not connect at all", as distinct from anything the server
 * said about the session.
 *
 * ⚠ They must be DISTINCT from the protocol codes the orchestrator's
 * model-policy self-heal matches (`invalid_model`, `invalid_value`,
 * `unknown_parameter`). A network failure reported as `invalid_value` would
 * add a perfectly good model to the effort-rejected set for the app session
 * and quietly downgrade its reasoning config, with nothing connecting the two.
 */
export const K_TRANSPORT_FAILURE_CODES: ReadonlySet<string> = new Set([
  "realtime_sdp_failed",
  "realtime_ice_failed",
  "realtime_datachannel_failed",
  "realtime_webrtc_unsupported",
]);

export interface IRealtimeTransport {
  readonly onConnected: Emitter<void>;
  readonly onDisconnected: Emitter<void>;
  readonly onMessage: Emitter<string>;
  readonly onError: Emitter<string>;
  /**
   * Agent audio playback started/drained (Unity `AgentSpeakingStateChanged`).
   *
   * One of the four terms of `AIInterfaceModel.isActivityActive`, and the only
   * one that outlives the response itself — audio playback continues after
   * `response.done`. A transport that never emits `true` drops Spark's crown
   * mid-answer and hides the Stop button while it is still speaking; one that
   * never emits `false` parks `waitForResponseIdleAsync` until its 60 s
   * timeout, so a workflow script's next `say` starts over the previous line.
   */
  readonly onAgentSpeakingChanged: Emitter<boolean>;

  /** See contract fact 5 — "a client event sent right now will arrive". */
  readonly isConnected: boolean;
  readonly isAgentSpeaking: boolean;
  /** No consumer today; see the file header before building a real meter. */
  readonly micInputLevel: number;

  /** Never rejects; resolves false on failure. See contract facts 1 and 2. */
  connectAsync(): Promise<boolean>;
  disconnect(): void;
  /** Never throws. See contract fact 3. */
  sendEvent(json: string): void;
  /**
   * Mute gates the MICROPHONE only — it never tears the audio path down, or
   * the server's VAD may never see the turn close.
   *
   * The first unmute is also when `getUserMedia` runs: the permission prompt
   * must never appear on page load or on connect, which is a deliberate
   * product behaviour and not an optimisation.
   */
  setMuted(muted: boolean, reason?: RealtimeMicrophoneReason): void;
  /** Browser autoplay policy — resume the output graph from a user gesture. */
  resumeAudioOutputIfNeeded(): void;
  /**
   * Barge-in: silence audio the model has ALREADY sent.
   *
   * `response.cancel` stops the server generating; it does not retract what is
   * already buffered locally or in flight, so a no-op here leaves Spark talking
   * over the user after they pressed Stop.
   */
  stopOutputAudioPlayback(): void;
  /**
   * ⚠ Runs on every RECONNECT, not only at shutdown — `teardownSession` calls
   * it from `stop()`, `restartAsync()`, the disconnect handler and the
   * failed-connect branch. Release everything: a leak here accumulates over a
   * session on a flaky link and surfaces much later as an unrelated failure.
   */
  dispose(): void;
}
